import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import qs from 'qs';
import moment from 'moment';

import _pathOr from 'lodash/fp/pathOr';
import _path from 'lodash/fp/path';
import _isEmpty from 'lodash/fp/isEmpty';
import _pluck from 'lodash/fp/pluck';
import _forEach from 'lodash/forEach';
import _cloneDeep from 'lodash/fp/cloneDeep';
import _range from 'lodash/range';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createTable from '../components/table';
import createFilters from '../../shared/filters';
import createSwitchControlMulti from '../../shared/switch-control/multi';
import { default as createCalendar, getDateRangeFromCalendar } from '../../shared/calendar';

const table = createTable({
	namespace: 'goodsList',
	stateNodeName: 'table',
	configureSort: {
		type: 'remote',
		resolverSortFn: request
	}
});

const filters = createFilters({
	namespace: 'goodsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [{
		name: 'categories',
		title: 'Categories',
		placeholder: 'Add categories',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.GOOD_CATEGORIES
		}
	}]
});

const switchControlMultiStatus = createSwitchControlMulti({
	namespace: 'goodsList',
	stateNodeName: 'switchControlMultiStatus',
	defaultValues: [
		{ name: 'active', placeholder: 'Active', active: true, className: 'bullet-two'},
		{ name: 'hidden', placeholder: 'Hidden', active: true, className: 'bullet-three'}
	]
});

const calendar = createCalendar({
	namespace: 'goodsList',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment('2019-03-01').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const FETCH_DATA_SUCCESS = 'goods/fetch-data-success';
const REMOVE_SUCCESS = 'goods/remove-data-success';
const REQUEST_ERROR = 'goods/request-error';
const TOGGLE_PROGRESS_BAR = 'goods/toggle-progress-bar';
const GOTO_PAGE_SUCCESS = 'goods/goto-page-success';
const APPLY = 'goods/apply';
const CLEAR = 'goods/clear';
const SET_SEARCH_QUERY = 'goods/set-search-query';

export const allowedPermissions = {
	'view': 'goods_view',
	'edit': 'goods_update',
	'create': 'goods_create',
	'delete': 'goods_delete'
};

const initialState = {
	data: null,
	showProgressBar: false,
	table: table.initialState,
	filters: filters.initialState,
	calendar: calendar.initialState,
	search: '',
	pagination: {
		currentPage: 1,
		perPage: 50,
		total: null
	},
	switchControlMultiStatus: switchControlMultiStatus.initialState,
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			const externalQuery = qs.parse(_pathOr({}, ['location', 'query'], props));
			const savedFilters = filters.selectors.getSavedFilters(getState(), 'goodsList');

			if (_isEmpty(externalQuery) && _path(['entity', 'filters', 'length'], savedFilters)) {
				return dispatch(replace(`/goods/?${qs.stringify(savedFilters)}`));
			}

			dispatch(request(getState, externalQuery))
				.then(() => {
					if (externalQuery.entity)	{
						const filtersList = externalQuery.entity.filters || [];
						dispatch(filters.actions.setFiltersFromExternalQuery(filtersList));
					}
				});
		},

		apply: () => (dispatch, getState) => {
			const searchForm = getFormValues('goodsSearchForm')(getState());
			const search = searchForm && searchForm.search;
			dispatch({
				type: APPLY,
				payload: search
			});
			dispatch(request(getState));
		},

		search: () => (dispatch, getState) => {
			dispatch(actions.setSearchQuery());
			dispatch(request(getState, dispatch));
		},
		removeGood: id => (dispatch, getState) => {
			const removingGood = getState().goodsList.data.find(g => g.id === id);
			return confirm(`Are you sure you want to DELETE good ${removingGood.title} with ID ${removingGood.id}`) &&
				http({
					url: `${endpoints.GOODS.SINGLE.replace('{id}', id)}`,
					method: 'delete',
					actions: [REMOVE_SUCCESS]
				})(dispatch)
					.then(() => actions.fetchData()(dispatch, getState));
		},

		setSearchQuery: () => (dispatch, getState) => {
			const searchForm = getFormValues('goodsSearchForm')(getState());
			const search = _pathOr('', ['search'])(searchForm);

			dispatch({
				type: SET_SEARCH_QUERY,
				payload: search
			});
		},

		clear: () => dispatch => {
			dispatch(actions.toggleProgressBar());
			dispatch(replace('/goods'));
			setTimeout(() => dispatch({ type: CLEAR }));
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		}),

		gotoPage: page => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());

			dispatch({
				type: GOTO_PAGE_SUCCESS,
				payload: page
			});

			request(getState)(dispatch);
		}

	},
	table.actions,
	filters.actions,
	switchControlMultiStatus.actions,
	calendar.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			if (action.payload.pathname.indexOf('/goods') !== -1) {
				return {
					...state,
					pagination: {
						...state.pagination,
						currentPage: initialState.pagination.currentPage
					},
					data: null
				};
			}
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			pagination: {
				...state.pagination,
				total: action.payload.data.total
			},
			showProgressBar: false,
			error: false
		}),
		[REMOVE_SUCCESS]: (state, action) => ({
			...state
		}),
		[APPLY]: (state, action) => ({
			...state,
			pagination: initialState.pagination,
			search: action.payload,
			showProgressBar: true
		}),
		[CLEAR]: () => initialState,
		[GOTO_PAGE_SUCCESS]: (state, action) => ({
			...state,
			pagination: {
				...state.pagination,
				currentPage: action.payload
			}
		}),
		[REQUEST_ERROR]: state => ({
			...state, showProgressBar: true
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[SET_SEARCH_QUERY]: (state, action) => ({ ...state, search: action.payload })
	},
	table.reducers,
	filters.reducers,
	switchControlMultiStatus.reducers,
	calendar.reducers
);

export default handleActions(reducers, _cloneDeep(initialState));

export const selectors = {
	getData: state => state.goodsList.data,
	getTable: table.selectors.getTable,
	getFilters: state => filters.selectors.getFilters(
		state => state.goodsList.filters,
		state => state
	)(state),
	getSearchData: state => ({ search: state.goodsList.search }),
	getPagination: state => {
		const { perPage, total, currentPage } = state.goodsList.pagination;
		return {
			pages: _range(Math.ceil(total / perPage)),
			currentPage,
			total
		};
	},
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.goodsList.calendar,
		state => state
	)(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getIntl: state => intl(state),
	getProgressBar: state => state.goodsList.showProgressBar,
	getSwitchControlMultiStatusItems: state => switchControlMultiStatus.selectors.getControls(state),
	getError: state => state.goodsList.error
};

function request(getState, externalQuery) {
	const {
		search, calendar,
		switchControlMultiStatus,
		filters: { selectedFilters },
		pagination: { perPage, currentPage },
		table: { sortByColumn }
	} = getState().goodsList;

	const limit = perPage;
	const offset = (currentPage - 1) * perPage;
	const activeQuickFiltersStatus = switchControlMultiStatus
		.filters.filter(f => f.active && f.name !== 'all')
		.map(f => f.name);

	let filters = {};
	const inverseFilters = {};

	if (selectedFilters && !_isEmpty(selectedFilters)) {
		selectedFilters.forEach(filter => {
			const values = _pluck('value')(filter.value);
			if (values.length) {
				const selectedFilterValues = filter.shouldInverse ? inverseFilters : filters;
				selectedFilterValues[filter.name] = values;
			}
		});
	}

	if (externalQuery && externalQuery.query && externalQuery.query.filters) {
		externalQuery.entity.filters && externalQuery.entity.filters.forEach(filter => {
			if (filter.is_inverted && JSON.parse(filter.is_inverted)) {
				inverseFilters[filter.name] = filter.values.map(f => f.value);
			}
		});

		externalQuery.query.filters && _forEach(externalQuery.query.filters, (value, key) => {
			if (inverseFilters[key]) return false;
			filters[key] = value;
		});
	}

	const query = qs.stringify({
		offset, limit, search, filters,
		quick_filters: { status: activeQuickFiltersStatus || '' },
		inverse_filters: inverseFilters,
		date_range: getDateRangeFromCalendar(calendar),
		sort: [sortByColumn.index, sortByColumn.direction]
	});

	return http({
		url: `${endpoints.GOODS.BASE}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, REQUEST_ERROR]
	});
}


// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/goods/list/index.js